<template>
    <div
        :class="classBlogPostPreview"
        class="blog-post-preview"
    >
        <ThLink :to="link">
            <div class="image">
                <CmsImage
                    :data="preview.featuredImage"
                    :is-eager="fullWidth"
                    fit="cover"
                />
            </div>

            <div class="description">
                <span class="date">
                    {{ dateFormatted }}
                </span>

                <h2 class="title">
                    {{ preview.title }}
                </h2>

                <p class="excerpt">
                    {{ preview.excerpt }}
                </p>

                <!-- TODO translate -->
                <span class="read-more">Read more</span>
            </div>
        </ThLink>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { BlogPostPreviewFragment } from '@/graphql/api'
import { useBlogPostDateFormatter } from '@/views/blog/composables/useBlogPostDate'
import { useRoutes } from '@/router/routes'

import CmsImage from '@/components/cms/CmsImage.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import { parseDate } from '@/utils/date'

const props = defineProps<{
    preview: BlogPostPreviewFragment
    fullWidth?: boolean
}>()

const routes = useRoutes()

const date = computed(() => props.preview && parseDate(props.preview.date))

const dateFormatted = useBlogPostDateFormatter(date)

const link = computed(() => routes.blog.post(props.preview))

const classBlogPostPreview = computed(() => ({
    'full-width': props.fullWidth,
}))
</script>

<style lang="scss" scoped>
.blog-post-preview {
    width: 100%;
    margin: 0 0 40px;

    .date {
        font-family: $effra-font-stack;
        font-size: 0.75rem;
        font-weight: 400;
        text-transform: uppercase;
        color: $grey-500;
    }

    .image {
        width: 100%;
        height: 300px;
        margin: 0 0 20px;

        @include min-width(768) {
            height: 400px;
        }

        @include min-width(1024) {
            height: 240px;
        }
    }

    .description {
        width: 740px;
        max-width: 100%;
        margin: 0 auto;
        padding: 0 20px;

        @include min-width(640) {
            padding: 0 10px;
        }

        @include min-width(1024) {
            width: initial;
        }

        .title {
            font-family: $parcel-font-stack;
            font-size: 1.18rem;
            line-height: 1.4em;
            letter-spacing: 0.01em;
            margin: 0 0 20px;
        }

        .excerpt {
            font-size: 1rem;
            line-height: 1.4em;
            color: $grey-500;
            margin: 0 0 20px;
        }

        .read-more {
            padding: 0 0 2px;
            font-family: $effra-font-stack;
            line-height: 1.313rem;
            letter-spacing: 0.05em;
            font-size: 0.875rem;
            font-weight: 400;
            text-transform: uppercase;
            color: var(--slate);
            border-bottom: 1px solid var(--slate);
        }
    }

    &.full-width {
        @include min-width(1024) {
            grid-column: 1 / span 3;
            padding: 0 0 60px;
            @include border-bottom;
        }

        .image {
            height: 300px;

            @include min-width(768) {
                height: 400px;
            }

            @include min-width(1024) {
                height: 450px;
            }
        }

        .description {
            width: 740px;
            max-width: 100%;

            .title {
                @include min-width(1024) {
                    font-size: 2.25rem;
                    margin: 0 0 20px;
                }
            }

            .excerpt {
                @include min-width(1024) {
                    font-size: 1rem;
                    line-height: 1.75em;
                    margin: 0 0 20px;
                }
            }
        }
    }
}
</style>
